import { HotmartRum } from '@hot-observability-js/react';
import { ProductFruitsUtil } from 'components/productFruits';
import cachedRequest from 'configs/cachedRequest';
import Settings from 'configs/Settings';
import Cookie from 'js-cookie';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import { Cookies } from 'modules/auth/constants';
import MembershipService from 'modules/membership/MembershipService';
import { UserStore } from 'modules/user';
import { ROLES } from 'modules/user/constants';

import packageJson from '../../../package.json';
import { toArray } from '../../helpers/Array';
import UserService from '../user/UserService';

import Auth from '@hotmart/oauth-ui';
import Request from '@hotmart/request';

const DEFAULT_ARRAY = [];

const ADMIN_ROLES = [
  ROLES.OWNER,
  ROLES.ADMIN,
  ROLES.CONTENT_EDITOR,
  ROLES.MODERATOR
];

const LOCAL_STORAGE_KEY = {
  USER: 'user'
};

class AuthService extends Auth {
  /**
   * @type { import('./types').Resource[] }
   */
  _resources = [];

  /**
   * @type { import('./types').CheckToken | undefined }
   */
  authCheckToken;

  refs = {
    integrationToken: 'hmClubIntegration',
    auth: '__amplify__oauthData',
    token: 'token',
    user: '__amplify__accountData',
    checkToken: '__amplify__checkTokenData',
    productToken: 'productToken',
    environment: Settings.environment
  };

  refreshRequest = (opt) => Request('auth.token.refresh', opt);
  checkRequest = (opt) => Request('auth.token.check', opt);
  loginRequest = (opt) => Request('auth.login', opt);
  logoutRequest = (opt) => Request('auth.logout', opt);

  get integrationToken() {
    const token =
      Cookie.get(Cookies.UserToken) || Cookie.get(Cookies.AttendantUserToken);

    return this.encodeToken(token);
  }

  get isLogged() {
    return !isEmpty(this.token);
  }

  set resources(resources) {
    this._resources = resources || [];
  }

  get resources() {
    return this._resources;
  }

  get userAreaData() {
    return (
      this.resources &&
      this.resources.find(
        (item) => item.resource.subdomain === MembershipService.subdomain
      )
    );
  }

  get roles() {
    return (this.userAreaData && this.userAreaData.roles) || DEFAULT_ARRAY;
  }

  get adminRole() {
    const userRoles = this.roles || DEFAULT_ARRAY;
    const adminRoles = intersection(ADMIN_ROLES, userRoles);

    return adminRoles[0];
  }

  get areaCode() {
    return this.refs && this.refs.userAreaCode;
  }

  set areaCode(areaCode) {
    this.refs.userAreaCode = areaCode;
  }

  get areaId() {
    return this.userAreaData && this.userAreaData.resource.userAreaId;
  }

  get cookieParams() {
    return {
      domain: Settings.cookieDomain
    };
  }

  get hasDashboardEnabled() {
    return this.isAdmin() || this.isOwner();
  }

  updateHeaders() {
    localStorage.setItem('token', this.token);
    Request.setDefaultHeaders({
      'token': this.token,
      'Club': MembershipService.subdomain,
      'x-app-name': packageJson.name
    });
  }

  redirectUser() {
    let url = window.location.href;

    url = url.replace(new RegExp(`${Settings.subpath}.*`, 'g'), '');
    if (url.includes(Settings.port)) {
      url = `https://${window.location.hostname}:${Settings.port}${Settings.subpath}/login`;
    }

    window.location.href = url;
  }

  isProducer() {
    return (
      this.isEditor() || this.isOwner() || this.isAdmin() || this.isModerator()
    );
  }

  isEditor() {
    return (
      (this.roles && this.roles.includes(ROLES.CONTENT_EDITOR)) ||
      UserStore.hasRole(ROLES.CONTENT_EDITOR)
    );
  }

  isOwner() {
    return (
      (this.roles && this.roles.includes(ROLES.OWNER)) ||
      UserStore.hasRole(ROLES.OWNER)
    );
  }

  isAdmin() {
    return (
      (this.roles && this.roles.includes(ROLES.ADMIN)) ||
      UserStore.hasRole(ROLES.ADMIN)
    );
  }

  isModerator() {
    return (
      (this.roles && this.roles.includes(ROLES.MODERATOR)) ||
      UserStore.hasRole(ROLES.MODERATOR)
    );
  }

  is(roles) {
    const expectedRoles = toArray(roles);
    const allRoles = this.roles || DEFAULT_ARRAY;

    return expectedRoles.some((role) => allRoles.includes(role));
  }

  hasAuthorities(...authorities) {
    const { authorities: userAuthorities = [] } = this.authCheckToken || {};

    return authorities.some((authority) => userAuthorities.includes(authority));
  }

  logoutUser = async () => {
    ProductFruitsUtil.destroy();

    // Logout from security and remove cookies
    await this.logout(Settings.cookieConfig);

    // Kill old admin session
    Cookie.remove('JSESSIONID', { path: '/admin' });

    localStorage.removeItem(LOCAL_STORAGE_KEY.USER);

    // Redirect to login
    this.redirectUser();
    return void 0;
  };

  async loginUser(username, password) {
    await this.login(username, password, Settings.cookieConfig);
    this.updateHeaders();
    await UserService.syncUserData();
    if (!this.areaCode) {
      try {
        await UserService.fetchUserAreaCode();
        // eslint-disable-next-line no-empty
      } catch {}
    }
    return void 0;
  }

  async loginUserWithToken() {
    await this.loginWithToken(this.token);
    this.updateHeaders();
    await UserService.syncUserData();
    if (!this.areaCode) {
      try {
        await UserService.fetchUserAreaCode();
        // eslint-disable-next-line no-empty
      } catch {}
    }
    return void 0;
  }

  async validateRefreshToken(forceRefresh = false) {
    try {
      await this.refreshToken(forceRefresh);
    } catch (e) {
      Request.cancelAll();
      this.resetAuth(Settings.cookieConfig);
      this.redirectUser();

      throw e;
    }
  }

  async validateToken() {
    if (UserService.hasInvalidToken === false) {
      return;
    }

    try {
      const checkToken = await this.checkRequest({ access_token: this.token });

      this.authCheckToken = checkToken;
      localStorage.setItem(
        LOCAL_STORAGE_KEY.USER,
        JSON.stringify({ user: this.authCheckToken })
      );
      this.updateHeaders();

      try {
        this.resources = await this.fetchResources();
      } catch {
        this.resources = this.authCheckToken?.resources || [];
      }

      if (!this.isProducer()) {
        this.logoutUser();
      }

      if (!this.areaCode) {
        await UserService.fetchUserAreaCode();
      }
    } catch (error) {
      HotmartRum.captureException(error);
    }
  }

  async refreshAccountData(forceRefresh, successCallback) {
    if (!this.token) {
      return;
    }

    await this.validateRefreshToken(forceRefresh);
    await this.validateToken();

    successCallback && successCallback(this.token);
  }

  async initNewSession(locale) {
    if (this.token) {
      try {
        await UserService.syncUserData(locale);
      } catch {
        this.resetAuth(Settings.cookieConfig);
      }
    } else {
      this.resetAuth(Settings.cookieConfig);
    }
  }

  getUrlPathWithoutBase() {
    const { pathname } = window.location;

    return pathname.replace(`${Settings.subpath}`, '');
  }

  fetchResources = () => {
    return cachedRequest(
      () => Request('auth.resources'),
      ['auth.resources', this.authCheckToken?.user_name]
    );
  };
}

export default new AuthService();
